/* eslint-disable jsx-a11y/no-onchange */
import { Spinner } from 'app/src/components/Spinner';
import React from 'react';
import { DeploymentsResponse } from 'app/gamma/src/types/responses';

import styles from './hydra-chooser.less'; // remove with FEPLAT-646
import { useFeatureFlag } from '@trello/feature-flag-client';
import { Select } from '@trello/nachos';

import { forTemplate } from 'app/common/lib/util/i18n';
const format = forTemplate('header_version_picker');

export interface ActiveHead {
  version: string | null;
  patch: number | null;
  name: string | null;
}

interface HydraChooserProps {
  loading: boolean;
  deployments: DeploymentsResponse;
  activeHead: ActiveHead;
  updateHead: (name: string) => void;
  updateVersion: (version: string) => void;
}

export const formatVersion = (version: string) =>
  version === '0.0' ? 'latest' : version;

export const headVersions = (
  deployments: { [key: string]: { [key: string]: boolean } },
  active: string,
): [string, boolean][] => {
  return Object.entries({ ...deployments[active] }).concat([['0.0', true]]);
};

// remove with FEPLAT-646
// also remove conditions / ternaries in render method
export const HydraChooser: React.FunctionComponent<HydraChooserProps> = ({
  loading,
  deployments,
  activeHead,
  updateHead,
  updateVersion,
}) => {
  const nachosSelectEnabled = useFeatureFlag('fep.nachos_select', false);
  return (
    <div>
      {loading && <Spinner centered />}
      {nachosSelectEnabled ? (
        <Select
          label={format('head')}
          onChange={({ value }: { value: string }) => updateHead(value)}
          value={activeHead.name}
          isSearchable
          options={[
            ...Object.entries(deployments)
              .filter(([key, deployment]) => {
                return Object.values(deployment).some((active) => active);
              })
              .map(([key, deployment]) => key)
              .sort((a, b) => a.localeCompare(b)),
          ]}
        />
      ) : (
        // remove with FEPLAT-646
        <div
          className={styles.blockButton}
          style={{ marginBottom: activeHead.name ? 10 : 0 }}
        >
          <label>{format('head')}</label>
          <span className={styles.value}>{activeHead.name}</span>
          <select
            onChange={(e) => updateHead(e.target.value)}
            value={activeHead.name || ''}
          >
            {[
              '',
              ...Object.entries(deployments)
                .filter(([key, deployment]) => {
                  return Object.values(deployment).some((active) => active);
                })
                .map(([key, deployment]) => key)
                .sort((a, b) => a.localeCompare(b)),
            ].map((name) => {
              return (
                <option
                  aria-selected={name === activeHead.name}
                  key={name}
                  value={name}
                >
                  {name}
                </option>
              );
            })}
          </select>
        </div>
      )}
      {activeHead.name &&
        (nachosSelectEnabled ? (
          <Select
            label={format('version')}
            onChange={({ value }: { value: string }) => updateVersion(value)}
            value={activeHead.version}
            isSearchable
            options={headVersions(deployments, activeHead.name).map(
              ([version, isEnabled]) => {
                if (version === activeHead.version) {
                  return {
                    value: version,
                    label: `${formatVersion(version)} ${format('current', {
                      version,
                    })}`,
                  };
                } else {
                  return {
                    value: version,
                    label: `${formatVersion(version)} ${
                      !isEnabled ? format('disabled', { version }) : ''
                    }`,
                    isDisabled: !isEnabled,
                  };
                }
              },
            )}
          />
        ) : (
          // remove with FEPLAT-646
          <div className={styles.blockButton}>
            <label>{format('version')}</label>
            <span className={styles.value}>
              {activeHead.version && formatVersion(activeHead.version)}
            </span>
            <select
              onChange={(e) => updateVersion(e.target.value)}
              value={activeHead.version || ''}
            >
              <option aria-selected={!activeHead.version} key={''} value={''} />
              {headVersions(deployments, activeHead.name).map(
                ([version, isEnabled]) => {
                  if (version === activeHead.version) {
                    return (
                      <option
                        aria-selected={version === activeHead.version}
                        key={version}
                        value={version}
                      >
                        {formatVersion(version)}&nbsp;
                        {format('current', { version })}
                      </option>
                    );
                  } else {
                    return (
                      <option
                        aria-selected={version === activeHead.version}
                        key={version}
                        value={version}
                      >
                        {formatVersion(version)}&nbsp;
                        {!isEnabled && format('disabled', { version })}
                      </option>
                    );
                  }
                },
              )}
            </select>
          </div>
        ))}
    </div>
  );
};
